import { useState } from 'react'
import {
  getDocumentDef, type ReportColumnDef, type ReportContext, type ReportDef, type ReportSummaryDef, type ReportWarning,
} from '@project/contracts'
import { Button, Icon, Modal, Money, Notice } from '../design/index.js'
import { Link } from '../app/Link.js'
import { paths } from '../app/router.js'
import { formatMoney } from './format.js'
import { reportValue } from './ReportGrid.js'

type Row = Record<string, unknown>

interface CardMetric {
  readonly label: string
  readonly type: ReportColumnDef['type']
  readonly value: unknown
  readonly signed: boolean
  /** A second figure beside the first, as a chip (REP1-010, REP1-011). */
  readonly chip?: { readonly type: ReportColumnDef['type']; readonly value: unknown; readonly signed: boolean }
  /** The figure measured against a target, drawn as a bar (REP1-006). */
  readonly progress?: { readonly pct: unknown; readonly target: unknown }
}

interface Card {
  readonly title: string
  readonly metrics: readonly CardMetric[]
}

/** The cards a summary layout declares, filled from the summary rows. */
export const summaryCards = (
  layout: ReportSummaryDef, columns: readonly ReportColumnDef[], rows: readonly Row[],
): Card[] => {
  const col = (key: string) => columns.find((c) => c.key === key)
  if (layout.cardPerRow) {
    const { titleKey, metrics } = layout.cardPerRow
    const keys = metrics ?? columns.map((c) => c.key).filter((k) => k !== titleKey)
    return rows.map((row) => ({
      title: String(row[titleKey] ?? ''),
      metrics: keys.map((key) => {
        const c = col(key)
        return { label: c?.label ?? key, type: c?.type, value: row[key], signed: Boolean(c?.signed) }
      }),
    }))
  }
  return (layout.cards ?? []).map((card) => ({
    title: card.title,
    metrics: card.metrics.map((m) => {
      const row = m.row !== undefined && layout.rowKey
        ? rows.find((r) => String(r[layout.rowKey!]) === m.row)
        : rows[0]
      const c = col(m.key)
      const out: CardMetric = { label: m.label, type: m.type ?? c?.type, value: row?.[m.key], signed: m.signed ?? Boolean(c?.signed) }
      return {
        ...out,
        ...(m.chip ? { chip: { type: m.chip.type ?? col(m.chip.key)?.type, value: row?.[m.chip.key], signed: Boolean(m.chip.signed) } } : {}),
        ...(m.progress ? { progress: { pct: row?.[m.progress.pctKey], target: row?.[m.progress.targetKey] } } : {}),
      }
    }),
  }))
}

/* The design system's categorical order; the last (neutral) is what is still open. */
const DONUT = ['var(--chart-1)', 'var(--chart-3)', 'var(--chart-6)']

/**
 * A few figures that make up one whole (RPT-007: unbilled, billed, open) as a
 * ring, with the total in the middle and a labelled legend — the colours are
 * never the only way to tell the parts apart.
 */
export const SummaryDonut = ({ donut, rows, rowKey }: {
  donut: NonNullable<ReportSummaryDef['donut']>; rows: readonly Row[]; rowKey?: string
}) => {
  const row = donut.row !== undefined && rowKey ? rows.find((r) => String(r[rowKey]) === donut.row) : rows[0]
  const parts = donut.segments.map((s, i) => ({
    ...s, value: Math.max(0, Number(row?.[s.key] ?? 0)), raw: String(row?.[s.key] ?? '0'), colour: DONUT[i % DONUT.length]!,
  }))
  const total = parts.reduce((n, p) => n + p.value, 0)
  const R = 54
  const C = 2 * Math.PI * R
  // A 2px surface gap between the parts, so neighbours never fuse.
  const gap = parts.filter((p) => p.value > 0).length > 1 ? 2 : 0
  let offset = 0
  return (
    <section className="rt-card rt-donut" aria-label={donut.title}>
      <h3 className="rt-card__title">{donut.title}</h3>
      <div className="rt-donut__body">
        <svg viewBox="0 0 140 140" width="140" height="140" role="img"
          aria-label={parts.map((p) => `${p.label} ${formatMoney(p.raw)}`).join(', ')}>
          <circle cx="70" cy="70" r={R} fill="none" stroke="var(--line)" strokeWidth="14" />
          {total > 0 ? parts.map((p) => {
            const length = (p.value / total) * C
            const dash = Math.max(0, length - gap)
            const el = (
              <circle key={p.key} cx="70" cy="70" r={R} fill="none" stroke={p.colour} strokeWidth="14"
                strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-offset} transform="rotate(-90 70 70)">
                <title>{`${p.label}: ${formatMoney(p.raw)} (${((p.value / total) * 100).toFixed(1)}%)`}</title>
              </circle>
            )
            offset += length
            return el
          }) : null}
          <text x="70" y="66" textAnchor="middle" className="rt-donut__centre-label">Total</text>
          <text x="70" y="84" textAnchor="middle" className="rt-donut__centre num">{formatMoney(total.toFixed(4))}</text>
        </svg>
        <ul className="rt-donut__legend">
          {parts.map((p) => (
            <li key={p.key}>
              <span className="rt-donut__swatch" style={{ background: p.colour }} aria-hidden="true" />
              <span className="rt-card__label">{p.label}</span>
              <span className="rt-card__value"><Money value={p.raw} /></span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

/** What the data says about these figures (RPT-050), with a way to the report that resolves it (RPT-007). */
export const ReportWarnings = ({ companyId, warnings, applied }: {
  companyId: string; warnings: readonly ReportWarning[] | undefined; applied: Readonly<Record<string, string>>
}) => {
  if (!warnings?.length) return null
  return (
    <div className="rt-warnings">
      {warnings.map((w) => (
        <Notice key={w.text} kind={w.tone === 'warn' ? 'warn' : 'info'}>
          {w.text}
          {w.report ? (
            <>
              {' '}
              <Link to={paths.report(companyId, w.report, applied['projectId'] ? { projectId: applied['projectId'] } : {})}>
                {w.reportLabel ?? 'Open the report'}
              </Link>
            </>
          ) : null}
        </Notice>
      ))}
    </div>
  )
}

/**
 * A figure against its target as a bar: "86.074% (₹1,15,55,123.30)" above it
 * on the left, the target on the right (REP1-006). Over the target the bar is
 * full and turns to the warning tone; the words carry the figure either way.
 */
const MetricProgress = ({ value, pct, target }: { value: unknown; pct: unknown; target: unknown }) => {
  const share = Number(pct)
  const known = pct !== null && pct !== undefined && pct !== '' && Number.isFinite(share)
  const width = known ? Math.max(0, Math.min(100, share)) : 0
  return (
    <div className="rt-progress">
      <div className="rt-progress__legend">
        <span className="num">{known ? `${share}%` : '—'} ({formatMoney(String(value ?? '0'))})</span>
        <span className="num">{formatMoney(String(target ?? '0'))}</span>
      </div>
      <div className="rt-progress__track" role="meter" aria-valuemin={0} aria-valuemax={100}
        aria-valuenow={Math.round(width)} aria-label={known ? `${share}% of the target` : 'No target'}>
        <span className={share > 100 ? 'rt-progress__fill rt-progress__fill--over' : 'rt-progress__fill'}
          style={{ width: `${width}%` }} />
      </div>
    </div>
  )
}

/** The Summary view as cards (RPT-036). */
export const SummaryCards = ({ layout, columns, rows }: {
  layout: ReportSummaryDef; columns: readonly ReportColumnDef[]; rows: readonly Row[]
}) => (
  <div className="rt-cards">
    {layout.donut ? <SummaryDonut donut={layout.donut} rows={rows} {...(layout.rowKey ? { rowKey: layout.rowKey } : {})} /> : null}
    {summaryCards(layout, columns, rows).map((card, i) => (
      // An untitled card is named by its first figure for assistive tech.
      <section key={`${i}:${card.title}`} className="rt-card" aria-label={card.title || card.metrics[0]?.label}>
        {card.title ? <h3 className="rt-card__title">{card.title}</h3> : null}
        {card.metrics.map((m) => (
          <div key={m.label} className="rt-card__metric">
            <div className="rt-card__line">
              <span className="rt-card__label">{m.label}</span>
              <span className="rt-card__value">
                {reportValue(m.type, m.value, m.signed) ?? '—'}
                {m.chip ? <span className="stat__chip rt-card__chip">{reportValue(m.chip.type, m.chip.value, m.chip.signed) ?? '—'}</span> : null}
              </span>
            </div>
            {m.progress ? <MetricProgress value={m.value} pct={m.progress.pct} target={m.progress.target} /> : null}
          </div>
        ))}
      </section>
    ))}
  </div>
)

/** The applied project and its stage, above the results (RPT-035). */
export const ContextStrip = ({ companyId, context }: { companyId: string; context: ReportContext | undefined }) => {
  const project = context?.project
  if (!project) return null
  const name = `${project.code} - ${project.name}`
  const stage = project.stage ?? (project.ongoing ? 'Ongoing' : 'Closed')
  return (
    <div className="rt-context" aria-label="Project">
      {project.documentId
        ? <Link to={paths.document(companyId, getDocumentDef('project')?.route ?? 'projects', project.documentId)}>{name}</Link>
        : <span>{name}</span>}
      <span className="stat__chip">{stage}</span>
    </div>
  )
}

/** The book button and how the figures are worked out (RPT-006). */
export const ReportHelp = ({ def }: { def: Pick<ReportDef, 'label' | 'help' | 'helpUrl' | 'blurb'> }) => {
  const [open, setOpen] = useState(false)
  if (!def.help && !def.helpUrl) return null
  return (
    <>
      <Button kind="ghost" icon={<Icon name="help" size={18} />} onClick={() => setOpen(true)}>Help</Button>
      {open ? (
        <Modal title={`${def.label}: how it is worked out`} subtitle={def.blurb} onClose={() => setOpen(false)}
          actions={<Button kind="ghost" onClick={() => setOpen(false)}>Close</Button>}>
          <div className="rt-help">
            {(def.help ?? '').split(/\n\s*\n/).filter(Boolean).map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
            {def.helpUrl ? (
              <p><a href={def.helpUrl} target="_blank" rel="noreferrer">Open the help document</a></p>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </>
  )
}
